import { useState } from "react";
import QuoteModal from "@/components/QuoteModal";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://images.pexels.com/photos/6804260/pexels-photo-6804260.jpeg";

export default function Hero() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  return (
    <section data-testid="hero-section" className="relative overflow-hidden bg-[#F4F9FC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 lg:pt-24 pb-20 lg:pb-32 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7"
        >
          <span data-testid="hero-eyebrow" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E5E5E5] text-[11px] uppercase tracking-[0.22em] text-[#0A0A0A]/80">
            <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.6} />
            Pune · India · LLP
          </span>
          <h1 data-testid="hero-title" className="font-heading mt-6 text-4xl sm:text-5xl lg:text-[64px] leading-[1.02] tracking-tight font-light text-[#0A0A0A]">
            Refining <em className="not-italic gold-gradient-text font-medium">Purity.</em>
            <br /> Building <em className="not-italic font-medium">Trust.</em>
          </h1>
          <p data-testid="hero-subtitle" className="mt-6 max-w-xl text-base lg:text-lg text-[#4B5563] leading-relaxed">
            Agroya Refinery LLP is one of India's emerging precious metals refining companies — specialising in
            refining, recovery, processing and manufacturing of high-purity gold and silver products for jewellers,
            bullion dealers, financial institutions and industrial customers.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              data-testid="hero-quote-btn"
              onClick={() => setQuoteOpen(true)}
              className="btn-gold h-12 px-6 rounded-md text-sm font-medium"
            >
              Get a Quote <ArrowRight className="ml-2 w-4 h-4" strokeWidth={1.8} />
            </Button>
            <a href="#contact">
              <Button data-testid="hero-contact-btn" className="btn-outline-ink h-12 px-6 rounded-md text-sm font-medium" variant="ghost">
                Contact Us
              </Button>
            </a>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-6 text-xs text-[#4B5563]">
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.6} /> 99.90 / 99.50 Assayed Purity</div>
            <div className="w-px h-4 bg-[#E5E5E5]" />
            <div>NABL · BIS · MSME Registered</div>
            <div className="w-px h-4 bg-[#E5E5E5]" />
            <div>Insured Logistics</div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="lg:col-span-5"
        >
          <div className="relative">
            <div className="absolute -inset-4 rounded-2xl" style={{ background: "radial-gradient(60% 60% at 30% 30%, rgba(212,175,55,0.18), transparent 70%)" }} />
            <div className="relative rounded-2xl overflow-hidden border border-[#E5E5E5] shadow-[0_30px_80px_rgba(212,175,55,0.18)]">
              <img
                src={HERO_IMG}
                alt="Molten gold pour in refinery"
                className="w-full h-[520px] object-cover"
                loading="eager"
              />
              <div className="absolute bottom-4 left-4 right-4 glass-card rounded-xl p-4 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-semibold">Live Operation</div>
                  <div className="font-heading text-sm text-[#0A0A0A] mt-0.5">Induction melt · Bay 02</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#4B5563]">Purity</div>
                  <div className="font-heading text-lg gold-gradient-text font-medium">99.90</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
      <QuoteModal open={quoteOpen} onOpenChange={setQuoteOpen} />
    </section>
  );
}
